import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    useJournalEntryQuery,
    useReverseJournalEntryMutation,
} from '@/api/queries/journal';
import type { ReverseEntryOptions } from '@/api/handlers/resources/journal/IJournalService';
import type { LocaleCode } from '@/api/models';
import { OPERATIONAL_ENTRY_CODE, operationalDocument } from '../../journalMeta';
import { useAccountLabelMap } from '../journalEntryLines';

type UseJournalEntryDrawerParams = {
    /** The entry to show; `null` closes the drawer. */
    entryId: string | null;
    locale: LocaleCode;
    onReversed: () => void;
};

/**
 * Logic behind the journal-entry detail drawer, so the component stays
 * render-only (the repo's triple pattern).
 *
 * Fetches the full entry, resolves account names from the chart, and owns the
 * reverse mutation plus its transient error. The error is cleared whenever the
 * shown entry changes — a failure belongs to exactly one entry and must never
 * be read against another.
 */
export const useJournalEntryDrawer = ({
    entryId,
    locale,
    onReversed,
}: UseJournalEntryDrawerParams) => {
    const { t } = useTranslation('pages');

    const entryQuery = useJournalEntryQuery(entryId);
    const entryResponse = entryQuery.data;
    const entry = entryResponse?.success ? entryResponse.response : null;

    const accountMap = useAccountLabelMap(locale);

    /**
     * The entry on the other side of a reversal, so the pair can be navigated.
     *
     * A reversal and its original are only meaningful together — reading one
     * without being able to reach the other is how a corrected mistake gets
     * mistaken for two unrelated entries. The server gives us the id in one
     * direction or the other, never both, so exactly one of these is set.
     *
     * The counterpart is fetched rather than looked up in the loaded page: the
     * pair can be months apart (here, an April original reversed at
     * month-end), so the other half is frequently not on screen. The query is
     * disabled when there is no link, and shares the cache with the drawer's
     * own read, so opening the counterpart afterwards is free.
     */
    const linkedId = entry?.reversesEntryId ?? entry?.reversedByEntryId ?? null;
    const linkedQuery = useJournalEntryQuery(linkedId);
    const linkedResponse = linkedQuery.data;
    const linked = linkedResponse?.success ? linkedResponse.response : null;

    const reversalLink = React.useMemo(() => {
        if (!entry || !linkedId) {
            return null;
        }
        return {
            id: linkedId,
            /** Which way round: this entry reverses another, or was reversed. */
            kind: entry.reversesEntryId
                ? ('reverses' as const)
                : ('reversedBy' as const),
            /**
             * `null` while the counterpart is still loading. The row renders
             * either way — the relationship is true before its number arrives,
             * and hiding the link until then would make it flicker in.
             */
            entryNumber: linked?.entryNumber ?? null,
        };
    }, [entry, linked, linkedId]);

    /**
     * Year-end entries cannot be reversed directly.
     *
     * `POST /journal-entries/{id}/reverse` refuses a `CLOSING` or `ROLLOVER`
     * entry with `YEAR_END_ENTRY` — *"reopen its fiscal year to undo it rather
     * than reversing it directly"*. The drawer used to offer Reverse on any
     * posted entry, so the only way to learn that was to click it and be told no.
     *
     * The button is hidden instead, with the remedy stated where the button was.
     * `onReverse` still surfaces the server's message if the refusal is ever
     * reached another way — hiding an action is a courtesy, not a guarantee.
     */
    const reverseBlockedReasonKey =
        entry?.source === 'CLOSING' || entry?.source === 'ROLLOVER'
            ? 'journal.drawer.reverseYearEnd'
            : null;

    /**
     * The document this entry came from, when it came from one.
     *
     * Reversing such an entry undoes its books while the sale / invoice /
     * payment stays on record, and the two stop agreeing (bos-api BOS-135). So
     * the drawer leads with the document instead, and keeps the books-only
     * reversal as a second, deliberate action behind a reason.
     */
    const sourceDocument = React.useMemo(
        () => (entry ? operationalDocument(entry) : null),
        [entry],
    );

    const reverseMutation = useReverseJournalEntryMutation();
    const [actionError, setActionError] = React.useState<string | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const [reason, setReason] = React.useState('');
    /**
     * The server called this entry operational even though the client did not
     * recognise its source (BOS-142).
     *
     * <p>`sourceDocument` only knows the origins this build was written with.
     * A newer one reaches the plain Reverse button, and the 409 that comes
     * back is the same fact stated by the only party that always knows it. It
     * switches the drawer into the guarded flow rather than printing the
     * server's sentence — which ends "or pass force=true", advice for a
     * caller holding an HTTP client, not for someone looking at a drawer.</p>
     */
    const [serverSaysOperational, setServerSaysOperational] =
        React.useState(false);

    // Clear transient state whenever the shown entry changes.
    React.useEffect(() => {
        setActionError(null);
        setIsConfirmOpen(false);
        setReason('');
        setServerSaysOperational(false);
    }, [entryId]);

    const runReverse = React.useCallback(
        async (options?: ReverseEntryOptions) => {
            if (!entry) {
                return;
            }
            setActionError(null);
            const result = await reverseMutation.mutateAsync({
                id: entry.id,
                options,
            });
            if (!result.success) {
                if (result.error.code === OPERATIONAL_ENTRY_CODE) {
                    // Not an error to report — a route to take instead.
                    setServerSaysOperational(true);
                    setIsConfirmOpen(true);
                    return;
                }
                setActionError(
                    result.error.message || t('journal.drawer.reverseFailed'),
                );
                return;
            }
            setIsConfirmOpen(false);
            setReason('');
            onReversed();
        },
        [entry, onReversed, reverseMutation, t],
    );

    const onReverse = React.useCallback(() => runReverse(), [runReverse]);

    const onConfirmForcedReverse = React.useCallback(
        () => runReverse({ force: true, reason: reason.trim() }),
        [reason, runReverse],
    );

    return {
        entry,
        accountMap,
        reversalLink,
        sourceDocument,
        isLoading: entryQuery.isLoading && entryId !== null,
        isReversing: reverseMutation.isPending,
        actionError,
        onReverse,
        /** The books-only path: open the confirm, type a reason, submit. */
        isConfirmOpen,
        onOpenConfirm: React.useCallback(() => setIsConfirmOpen(true), []),
        onCloseConfirm: React.useCallback(() => setIsConfirmOpen(false), []),
        reason,
        onReasonChange: setReason,
        onConfirmForcedReverse,
        canConfirmForcedReverse: reason.trim().length > 0,
        canReverse:
            entry?.status === 'POSTED' &&
            reverseBlockedReasonKey === null &&
            sourceDocument === null &&
            !serverSaysOperational,
        /** The books-only action is offered where the plain one is withheld. */
        canForceReverse:
            entry?.status === 'POSTED' &&
            reverseBlockedReasonKey === null &&
            (sourceDocument !== null || serverSaysOperational),
        reverseBlockedReasonKey,
    };
};

export type JournalEntryDrawerModel = ReturnType<typeof useJournalEntryDrawer>;
